import { IS_NATIVE } from '@/config/platform';
import { onAppResume } from '@/utils/app-lifecycle';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';

export default function TanstackProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 60000,
            gcTime: 60000
          }
        }
      }),
    []
  );

  // Le système gèle une application mobile passée en arrière-plan : rien ne
  // repart tout seul au réveil, et avec un `staleTime` d'une minute tout ce
  // qu'on rouvre après une pause est périmé — mais affiché comme s'il ne
  // l'était pas. On rafraîchit ce qui est à l'écran au retour.
  //
  // Uniquement sur mobile : sur le web et le bureau l'application n'a jamais
  // cessé de tourner, `refetchOnWindowFocus: false` y est un choix délibéré, et
  // le lever ferait repartir des requêtes à chaque aller-retour entre fenêtres.
  useEffect(() => {
    if (!IS_NATIVE) return;
    return onAppResume(() => {
      void queryClient.invalidateQueries({ type: 'active' });
    });
  }, [queryClient]);

  return <QueryClientProvider client={queryClient}>{children as JSX.Element}</QueryClientProvider>;
}
